// #region Imports

import chroma from 'chroma-js';
import type { CssColor } from '../../../Utils/CssColor';
import type { ThemePalette } from '../../../Utils/ThemePalette';
import type { ThemeScheme } from '../../../Utils/ThemeScheme';
import type { IThemeGeneratorStrategy } from '../../interfaces/IThemeGeneratorStrategy';

// #endregion

/**
 * Abstract theme generator strategy that operates in the **LCH** (Luminance, Chroma, Hue)
 * perceptual color space.
 *
 * @remarks
 * LCH is a perceptually uniform color space where `L*` (lightness 0–100) maps linearly
 * to how humans perceive brightness. This makes it ideal for reliable mode detection
 * and color adaptation.
 *
 * Concrete themes (Joy, Memphis, Cosmopolitan, …) extend this class to inherit
 * the shared color-science logic and only override behavior where their visual
 * language demands it.
 *
 * @public
 * @abstract
 */
export abstract class LchThemeGeneratorStrategy implements IThemeGeneratorStrategy {

    // #region Fields

    private static readonly _LUMINANCE_THRESHOLD = 0.5;

    // #endregion

    // #region Methods

    /**
     * Determines whether a color naturally belongs to the given mode's luminance range.
     *
     * @remarks
     * Uses WCAG relative luminance (0 = black, 1 = white).
     * A color with luminance > 0.5 is considered "light"; ≤ 0.5 is considered "dark".
     *
     * @public
     * @param color - The color to check
     * @param mode - The target color mode ('dark' | 'light')
     * @returns `true` if the color is naturally compatible with the given mode
     */
    public isColorInModeSpectrum(color: CssColor, mode: 'dark' | 'light'): boolean {
        const luminance = chroma(color).luminance();

        return mode === 'light'
            ? luminance > LchThemeGeneratorStrategy._LUMINANCE_THRESHOLD
            : luminance <= LchThemeGeneratorStrategy._LUMINANCE_THRESHOLD;
    }

    /**
     * Adapts a color to the opposite mode by symmetrically inverting its perceptual
     * lightness in the LCH color space (`L* → 100 - L*`) while preserving hue and chroma.
     *
     * @remarks
     * The inversion is symmetric: a light color becomes dark and vice-versa, regardless
     * of the direction. Therefore no target mode parameter is required.
     *
     * The inverted lightness is clamped to [5, 95] to avoid pure black / pure white,
     * which cannot carry chromatic information.
     * Subclasses may override this method to apply theme-specific post-processing
     * (e.g., chroma reduction for muted palettes).
     *
     * @public
     * @param color - The color to adapt
     * @returns The adapted color with inverted lightness
     */
    public adaptColorToMode(color: CssColor): CssColor {
        const c = chroma(color);
        const [l, chromaVal, h] = c.lch();
        const invertedL = Math.min(Math.max(100 - l, 5), 95);

        return chroma.lch(invertedL, chromaVal, isNaN(h) ? 0 : h).hex() as CssColor;
    }

    /**
     * Generates a color palette from a base color for the given mode.
     *
     * @public
     * @param baseColor - The base/accent color
     * @param mode - 'light' or 'dark' mode
     * @returns The generated theme palette with shade steps 0–900
     */
    public generatePalette(baseColor: CssColor, mode: 'dark' | 'light'): Required<ThemePalette> {
        if (mode === 'light') {
            const baseLight = chroma('#ffffff');
            const baseDark = chroma('#000000');
            const palette: Record<string, CssColor> = {};
            const steps = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
            const amounts = [0, 12, 30, 50, 70, 85, 100, 87, 70, 54, 25];

            steps.forEach((step, i) => {
                const base = step <= 500 ? baseLight : baseDark;
                palette[`${step}`] = chroma.mix(base, baseColor, amounts[i] / 100, 'rgb').hex() as CssColor;
            });

            return {
                "0": palette['0'],
                "50": palette['50'],
                "100": palette['100'],
                "200": palette['200'],
                "300": palette['300'],
                "400": palette['400'],
                "500": palette['500'],
                "600": palette['600'],
                "700": palette['700'],
                "800": palette['800'],
                "900": palette['900']
            };
        }

        const baseLight = chroma('#ffffff');
        const baseDark = chroma('#000000');
        const palette: Record<string, CssColor> = {};
        const steps = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
        const amounts = [0, 15, 30, 45, 60, 75, 90, 100, 70, 54, 25];

        steps.forEach((step, i) => {
            const base = step <= 600 ? baseDark : baseLight;
            palette[`${step}`] = chroma.mix(base, baseColor, amounts[i] / 100, 'rgb').hex() as CssColor;
        });

        return {
            "0": palette['0'],
            "50": palette['50'],
            "100": palette['100'],
            "200": palette['200'],
            "300": palette['300'],
            "400": palette['400'],
            "500": palette['500'],
            "600": palette['600'],
            "700": palette['700'],
            "800": palette['800'],
            "900": palette['900']
        };
    }

    /**
     * Generates a semantic color scheme based on a base color and mode.
     *
     * @public
     * @param baseColor - The base/accent color
     * @param mode - 'light' or 'dark' mode
     * @returns The generated theme scheme with semantic color roles
     */
    public generateScheme(baseColor: CssColor, mode: 'dark' | 'light'): Required<ThemeScheme> {
        const baseChroma = chroma(baseColor);
        const baseLuminance = baseChroma.luminance();

        /**
         * Adjusts color brightness while respecting min/max luminance bounds.
         * Positive amount = brighten, negative amount = darken.
         */
        const adjustColor = (color: CssColor, amount: number, minLuminance = 0.02, maxLuminance = 0.98): CssColor => {
            const c = chroma(color);
            const factor = Math.abs(amount) / 25.5;

            let result = amount > 0
                ? c.brighten(factor)
                : c.darken(factor);

            // Clamp luminance to avoid pure black/white
            const resultLuminance = result.luminance();
            if (resultLuminance < minLuminance) {
                result = chroma(result).luminance(minLuminance);
            } else if (resultLuminance > maxLuminance) {
                result = chroma(result).luminance(maxLuminance);
            }

            return result.hex() as CssColor;
        };

        /**
         * Generates foreground/contrast color with guaranteed readability.
         * Light mode: dark foreground (#1a1a1a - #2d2d2d)
         * Dark mode: light foreground (#e8e8e8 - #f5f5f5)
         */
        const generateForeground = (): CssColor => {
            if (mode === 'light') {
                // Light mode needs dark text - derive from base but ensure darkness
                const darkened = baseChroma.darken(4).saturate(-0.3);
                const targetLuminance = Math.min(darkened.luminance(), 0.08);

                return chroma(darkened).luminance(targetLuminance)
                    .hex() as CssColor;
            }

            // Dark mode needs light text - derive from base but ensure brightness
            const lightened = baseChroma.brighten(4).saturate(-0.3);
            const targetLuminance = Math.max(lightened.luminance(), 0.85);

            return chroma(lightened).luminance(targetLuminance)
                .hex() as CssColor;
        };

        /**
         * Generates surface color (slightly elevated from background).
         * Light mode: slightly darker or lighter based on background
         * Dark mode: slightly lighter
         */
        const generateSurface = (): CssColor => {
            if (mode === 'light') {
                // For very light backgrounds, make surface slightly darker for elevation
                return baseLuminance > 0.9
                    ? adjustColor(baseColor, -8)
                    : adjustColor(baseColor, 8);
            }

            // Dark mode: surface is slightly lighter than background
            return adjustColor(baseColor, 15);
        };

        /**
         * Generates highlight color for hover/focus states.
         */
        const generateHighlight = (): CssColor => mode === 'light'
            ? adjustColor(baseColor, -15, 0.05, 0.95)
            : adjustColor(baseColor, 20, 0.05, 0.95);

        /**
         * Generates middlelight color (medium contrast).
         */
        const generateMiddlelight = (): CssColor => mode === 'light'
            ? adjustColor(baseColor, -30, 0.1, 0.9)
            : adjustColor(baseColor, 35, 0.1, 0.9);

        /**
         * Generates lowlight color (lower contrast).
         */
        const generateLowlight = (): CssColor => mode === 'light'
            ? adjustColor(baseColor, -50, 0.15, 0.85)
            : adjustColor(baseColor, 55, 0.15, 0.85);

        /**
         * Generates disabled state color.
         */
        const generateDisabled = (): CssColor => {
            const desaturated = baseChroma.desaturate(2);

            return mode === 'light'
                ? chroma(desaturated).darken(0.5)
                    .hex() as CssColor
                : chroma(desaturated).brighten(0.5)
                    .hex() as CssColor;
        };

        const foreground = generateForeground();

        return {
            surface: generateSurface(),
            background: baseColor,
            foreground: foreground,
            highlight: generateHighlight(),
            middlelight: generateMiddlelight(),
            lowlight: generateLowlight(),
            transparent: `${baseColor}00` as CssColor,
            semiTransparent: chroma(baseColor).alpha(0.67)
                .hex() as CssColor,
            disabled: generateDisabled(),
            contrast: foreground,
            selection: generateHighlight()
        };
    }

    // #endregion

}
